/**
 * The decisions around encoding, kept apart from the encoding itself so node
 * can test them: whether this browser can burn at all, which output it makes,
 * and what the file is called. The worker asks the browser the questions
 * (VideoEncoder.isConfigSupported through mediabunny's canEncodeVideo) and
 * this file turns the answers into a plan.
 *
 * H.264 in MP4 first: every player and app plays it. VP9 in WebM when this
 * browser cannot encode H.264 at the video's size. Neither means no output,
 * and the page says to use a current Chrome, Edge, Safari or Firefox. There is
 * no server fallback: the video never leaves the computer.
 */

export interface OutputPlan {
  readonly container: 'mp4' | 'webm';
  readonly codec: 'avc' | 'vp9';
  readonly mime: 'video/mp4' | 'video/webm';
  readonly extension: 'mp4' | 'webm';
}

export const MP4_H264: OutputPlan = {
  container: 'mp4',
  codec: 'avc',
  mime: 'video/mp4',
  extension: 'mp4',
};

export const WEBM_VP9: OutputPlan = {
  container: 'webm',
  codec: 'vp9',
  mime: 'video/webm',
  extension: 'webm',
};

/** What the browser said it can encode at this video's size. */
export interface EncodeSupport {
  readonly avc: boolean;
  readonly vp9: boolean;
}

export function chooseOutput(support: EncodeSupport): OutputPlan | null {
  if (support.avc) return MP4_H264;
  if (support.vp9) return WEBM_VP9;
  return null;
}

/** The globals the burner needs before it can ask anything finer. */
export interface BrowserFeatures {
  readonly VideoEncoder: unknown;
  readonly VideoDecoder: unknown;
  readonly VideoFrame: unknown;
  readonly OffscreenCanvas: unknown;
  readonly Worker: unknown;
}

export function canBurnHere(g: Partial<BrowserFeatures>): boolean {
  return (
    typeof g.VideoEncoder === 'function' &&
    typeof g.VideoDecoder === 'function' &&
    typeof g.VideoFrame === 'function' &&
    typeof g.OffscreenCanvas === 'function' &&
    typeof g.Worker === 'function'
  );
}

/** H.264 at 4:2:0 needs even dimensions; an odd one is rounded up. */
export function evenSize(n: number): number {
  const r = Math.max(2, Math.round(n));
  return r % 2 === 0 ? r : r + 1;
}

/** "holiday.mov" -> "holiday-subtitled.mp4". */
export function burnedName(videoName: string, plan: OutputPlan): string {
  const base =
    videoName
      .replace(/\.[^./\\]+$/, '')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .trim() || 'video';
  return `${base}-subtitled.${plan.extension}`;
}
